<script setup>
import { OPTION_1_WORDING, OPTION_2_CONFIRM_LABEL, OPTION_2_STATEMENT, TWO_WEEK_NOTE } from '~/constants/additional-enquiries'

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
})

// Which card is highlighted. Kept separate from the submitted value because
// Option 2 is only a valid answer once the client has ticked the risk
// confirmation - until then the input stays empty and validation blocks submit.
const selectedOption = ref(props.context?._value ?? null)
const riskAccepted = ref(props.context?._value === 'option_2')

// The form disables its inputs once submitted, or when opened read only. The
// recorded choice must not look changeable.
const isReadOnly = computed(() => props.context?.disabled === true)

function write(value) {
  if (props.context.node && typeof props.context.node.input === 'function') {
    props.context.node.input(value)
  }
}

function selectOption(option) {
  if (isReadOnly.value) {
    return
  }

  selectedOption.value = option

  if (option !== 'option_2') {
    riskAccepted.value = false
    write(option)

    return
  }

  write(riskAccepted.value ? 'option_2' : null)
}

watch(riskAccepted, (accepted) => {
  if (selectedOption.value === 'option_2') {
    write(accepted ? 'option_2' : null)
  }
})
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="rounded-lg border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
      <p class="mb-6 text-sm leading-relaxed text-gray-600">
        Our role is to explain your options -
        <strong class="text-gray-900">the choice is yours</strong>
        . Option 1 gives you an early solution that reduces your risk to
        <strong class="text-gray-900">5%</strong>
        , while Option 2 adds delay - one of the biggest reasons why
        <strong class="text-gray-900">30% of buyers fail to complete every year</strong>
        , wasting months of time and money. If it helps,
        <strong class="text-gray-900">99% of our clients do this</strong>
        , as it helps them reduce being one of the almost one in three who fail to complete.
      </p>

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <button
          type="button"
          class="flex flex-col gap-3 rounded-xl border-2 p-5 text-left transition-colors"
          :class="[
            selectedOption === 'option_1' ? 'border-primary ring-2 ring-primary/20' : 'border-gray-200',
            isReadOnly ? 'cursor-default opacity-90' : 'hover:border-primary/50',
          ]"
          :disabled="isReadOnly"
          @click="selectOption('option_1')"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="rounded-full bg-primary px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-white">99% of our clients choose Option 1</span>
            <span
              class="flex h-5 w-5 items-center justify-center rounded-full border-2"
              :class="selectedOption === 'option_1' ? 'border-primary' : 'border-gray-300'"
            >
              <span v-if="selectedOption === 'option_1'" class="h-2.5 w-2.5 rounded-full bg-primary" />
            </span>
          </div>
          <h3 class="text-base font-bold text-gray-900">Choose Option 1</h3>
          <p class="border-l-2 border-primary/40 pl-3 text-sm italic text-gray-600">"{{ OPTION_1_WORDING }}"</p>
          <p class="text-xs text-gray-500">99% of our clients do this, as it helps them reduce being one of the almost one in three who fail to complete.</p>
        </button>

        <button
          type="button"
          class="flex flex-col gap-3 rounded-xl border-2 p-5 text-left transition-colors"
          :class="[
            selectedOption === 'option_2' ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-200',
            isReadOnly ? 'cursor-default opacity-90' : 'hover:border-red-300',
          ]"
          :disabled="isReadOnly"
          @click="selectOption('option_2')"
        >
          <div class="flex items-center justify-between gap-2">
            <span class="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-red-700">Higher risk</span>
            <span
              class="flex h-5 w-5 items-center justify-center rounded-full border-2"
              :class="selectedOption === 'option_2' ? 'border-red-500' : 'border-gray-300'"
            >
              <span v-if="selectedOption === 'option_2'" class="h-2.5 w-2.5 rounded-full bg-red-500" />
            </span>
          </div>
          <h3 class="text-base font-bold text-gray-900">Choose Option 2</h3>
          <p class="border-l-2 border-red-200 pl-3 text-sm italic text-gray-600">
            "I understand that by choosing the higher risk Option 2, my case is more likely to be delayed…"
          </p>
          <p class="text-xs text-gray-500">Selecting this option will show the Option 2 higher risk statement to confirm.</p>
        </button>
      </div>

      <div v-if="selectedOption === 'option_2'" class="mt-4 rounded-xl border border-red-200 bg-red-50 p-5">
        <h4 class="text-sm font-bold text-red-700">Option 2 higher risk statement</h4>
        <p class="mt-0.5 text-xs font-semibold text-red-800">Please confirm before you choose this option</p>
        <p class="mt-3 text-sm text-red-900">{{ OPTION_2_STATEMENT }}</p>
        <label class="mt-4 flex cursor-pointer items-start gap-3 text-sm font-semibold text-gray-900">
          <input v-model="riskAccepted" type="checkbox" :disabled="isReadOnly" class="mt-0.5 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500" />
          {{ OPTION_2_CONFIRM_LABEL }}
        </label>
      </div>

      <p class="mt-5 text-xs text-gray-500">
        <template v-if="isReadOnly">This choice has been recorded and cannot be changed.</template>
        <template v-else-if="!selectedOption">Select an option above to continue.</template>
        <template v-else-if="selectedOption === 'option_2' && !riskAccepted">Please tick the confirmation above to continue.</template>
        <template v-else>Sign below to record your choice.</template>
      </p>
    </div>

    <div class="rounded-lg border border-dashed border-gray-300 bg-white p-4 text-xs text-gray-500">
      <strong class="text-gray-900">What if I forget to reply, or I want to think about it for two weeks?</strong>
      {{ TWO_WEEK_NOTE }}
    </div>
  </div>
</template>
